import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, addOrderToDb, getNextPoNumber } from '../firebase';
import { getPersistableMockupUrl } from '../utils/fileHelpers';
import { DOC_TYPES, ORDER_STATUSES } from '../firebase/schema';
import styled from '@emotion/styled';
import { formatDate } from '../utils/dateFormatter';
import Modal from '../components/Modal';
import OrderIntakeForm from '../components/OrderIntakeForm';
import { FaEye, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useTheme } from '@emotion/react';
import { useFacility } from '../context/FacilityContext';
import { resolveFacilityId } from '../utils/facilities';

// Styled components (same as before)
const Container = styled.div`
  padding: 80px 1% 20px 1%;
  width: 98%;
  margin: 0 auto;
  background: var(--al-bg);
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-family: 'Montserrat', sans-serif;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: white;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.02em;
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    cursor: pointer;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const RefreshButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s;
  letter-spacing: 0.05em;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'AI_PARSED': return '#e3f2fd';  // Light blue
      case 'APPROVED': return '#e8f5e9';   // Light green
      case 'REJECTED': return '#ffebee';   // Light red
      default: return '#f5f5f5';           // Light gray
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'AI_PARSED': return '#1976d2';  // Blue
      case 'APPROVED': return '#2e7d32';   // Green
      case 'REJECTED': return '#c62828';   // Red
      default: return '#757575';           // Gray
    }
  }};
`;

// Modal styled components
const ModalContainer = styled.div`
  padding: 30px;
  max-width: 1200px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--al-bg);
  color: white;
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 15px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
`;

const EmailContent = styled.pre`
  background: var(--al-surface);
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  white-space: pre-wrap;
`;

const ParsedData = styled.pre`
  background: var(--al-surface);
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.secondary ? `
    background: #6c757d;
    color: white;
    &:hover {
      background: #5a6268;
    }
  ` : props.danger ? `
    background: #dc3545;
    color: white;
    &:hover {
      background: #c82333;
    }
  ` : `
    background: ${props.theme.colors.primary};
    color: black;
    &:hover {
      background: ${props.theme.colors.primaryDark};
    }
  `}
`;

const ConfidenceBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    const confidence = props.confidence * 100;
    if (confidence >= 90) return '#e8f5e9';  // Light green
    if (confidence >= 70) return '#fff3e0';  // Light orange
    return '#ffebee';  // Light red
  }};
  color: ${props => {
    const confidence = props.confidence * 100;
    if (confidence >= 90) return '#2e7d32';  // Green
    if (confidence >= 70) return '#f57c00';  // Orange
    return '#c62828';  // Red
  }};
`;

const OrderInbox = () => {
  const { facilityId: activeFacilityId } = useFacility();
  const navigate = useNavigate();
  const theme = useTheme();
  const [aiOrders, setAiOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [processingEmails, setProcessingEmails] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('online');
  const [debugInfo, setDebugInfo] = useState(null);

  // Note: Offline persistence is now configured in firebase.js using persistentLocalCache
  // The deprecated enableIndexedDbPersistence has been removed

  // Handle network status
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network is online');
      setNetworkStatus('online');
      enableNetwork(db).then(() => {
        console.log('Firestore network enabled');
        fetchAiOrders();
      });
    };

    const handleOffline = () => {
      console.log('Network is offline');
      setNetworkStatus('offline');
      disableNetwork(db).then(() => {
        console.log('Firestore network disabled');
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchAiOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      setProcessingEmails(true);
      
      // First, try to check for new emails and process them (optional - don't fail if this doesn't work)
      try {
        // Use full Cloud Run URL in production, proxy in development
        const apiEndpoint = process.env.NODE_ENV === 'production'
          ? 'https://processemailsmanually-dxxoy2pfha-uc.a.run.app'
          : '/processEmailsManually';
        
        console.log('Attempting to process emails...', { env: process.env.NODE_ENV, endpoint: apiEndpoint });
        const processResponse = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Email processing response status:', processResponse.status);
        console.log('Email processing response headers:', Object.fromEntries(processResponse.headers.entries()));
        
        // Get response text first to see what we're dealing with
        const responseText = await processResponse.text();
        console.log('Email processing response status:', processResponse.status);
        console.log('Response content-type:', processResponse.headers.get('content-type'));
        console.log('Response text (first 2000 chars):', responseText.substring(0, 2000));
        
        if (processResponse.ok) {
          // Try to parse as JSON regardless of content-type
          let processResult = null;
          try {
            processResult = JSON.parse(responseText);
            console.log('Email processing result (parsed as JSON):', processResult);
            
            // Store debug info for display
            setDebugInfo({
              success: processResult.success !== false,
              message: processResult.message || 'Emails processed',
              resultsCount: processResult.results?.length || 0,
              results: processResult.results,
              totalOrders: processResult.totalOrders
            });
            
            if (processResult.message) {
              console.log('Processing message:', processResult.message);
            }
            if (processResult.results && processResult.results.length > 0) {
              console.log(`Processed ${processResult.results.length} emails:`, processResult.results);
            } else {
              console.log('⚠️ No emails were processed. Check Gmail query and labels.');
            }
          } catch (parseError) {
            // Not JSON - might be HTML error page
            console.error('Failed to parse response as JSON:', parseError);
            console.log('Full response text:', responseText);
            setDebugInfo({
              success: false,
              error: `HTTP ${processResponse.status}: Response is not JSON`,
              response: responseText.substring(0, 1000),
              contentType: processResponse.headers.get('content-type')
            });
          }
        } else {
          console.error('Email processing endpoint error:', {
            status: processResponse.status,
            statusText: processResponse.statusText,
            body: responseText.substring(0, 1000)
          });
          
          // Try to parse error as JSON
          let errorData = null;
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            // Not JSON, use raw text
          }
          
          setDebugInfo({
            success: false,
            error: `HTTP ${processResponse.status}: ${processResponse.statusText}`,
            details: errorData || responseText.substring(0, 500)
          });
        }
      } catch (emailError) {
        console.error('Error calling email processing endpoint:', emailError);
        console.error('Error details:', {
          message: emailError.message,
          stack: emailError.stack,
          name: emailError.name
        });
        setDebugInfo({
          success: false,
          error: emailError.message,
          type: 'Network/Connection Error',
          details: 'Could not reach email processing endpoint. Check if Cloud Function is deployed and accessible.'
        });
        // Continue anyway - we can still show existing orders from Firestore
      }
      
      // Then fetch all orders from Firestore
      const ordersRef = collection(db, 'aiParsedOrders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      
      console.log('Fetching orders from Firestore...');
      const querySnapshot = await getDocs(q);
      console.log('Got orders from Firestore:', querySnapshot.size);
      
      const allOrders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('All orders from Firestore:', allOrders.length);
      console.log('Order statuses:', allOrders.map(o => ({ 
        id: o.id, 
        status: o.status,
        customerName: o.parsedOrder?.customerName || 'N/A',
        createdAt: o.createdAt
      })));
      
      // Filter for AI_PARSED status
      const ordersData = allOrders.filter(order => order.status === 'AI_PARSED');

      console.log('Filtered AI_PARSED orders:', ordersData.length);
      console.log('Processed orders:', ordersData);
      
      if (ordersData.length === 0 && allOrders.length > 0) {
        console.warn('⚠️ Found orders in Firestore but none with status AI_PARSED');
        const statuses = [...new Set(allOrders.map(o => o.status))];
        console.warn('Order statuses found:', statuses);
        console.warn('Sample order data:', allOrders[0]);
        
        // Show a helpful message
        setError(`Found ${allOrders.length} orders but none with status 'AI_PARSED'. Statuses found: ${statuses.join(', ')}`);
      } else if (ordersData.length > 0) {
        setError(null); // Clear error if we found orders
      }
      
      setAiOrders(ordersData);
    } catch (error) {
      console.error('Error fetching AI orders:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setProcessingEmails(false);
    }
  };

  useEffect(() => {
    fetchAiOrders();
  }, []);

  const handleViewOrder = (order) => {
    // Debug logging
    console.log('Opening order:', order);
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleApproveOrder = async (formData) => {
    try {
      console.log('Approving order with form data:', formData);
      
      const originalAiOrder = formData.originalAiOrder;
      if (!originalAiOrder?.id) {
        throw new Error('Original AI order ID not found');
      }

      // Calculate totals from lines
      const totalQuantity = formData.lines.reduce((sum, line) => {
        const lineQty = Object.values(line.sizes || {}).reduce((s, q) => s + (Number(q) || 0), 0);
        return sum + lineQty;
      }, 0);

      const subtotal = formData.lines.reduce((sum, line) => {
        const lineQty = Object.values(line.sizes || {}).reduce((s, q) => s + (Number(q) || 0), 0);
        return sum + (lineQty * (Number(line.price) || 0));
      }, 0);

      const cogsTotal = formData.lines.reduce((sum, line) => {
        const lineQty = Object.values(line.sizes || {}).reduce((s, q) => s + (Number(q) || 0), 0);
        return sum + (lineQty * (Number(line.cogs) || 0));
      }, 0);

      // Generate PO number to match normal New Order flow
      const poNumber = await getNextPoNumber();

      // Upload mockups / production artwork similar to NewOrder2 so we never save blob URLs
      const updatedLines = await Promise.all(formData.lines.map(async (line) => {
        const designNameSanitized = (line.design || line.designName || 'design').replace(/[^a-zA-Z0-9_-]/g, '_');
        let mockupUrl = null;
        let fileToUpload = null;

        // ===== HANDLE MOCKUP FILES =====
        // Check for legacy artworkFile
        if (line.artworkFile) {
          fileToUpload = line.artworkFile;
        }
        // Check for File object stored with mockup (when blob URL was created)
        else if (line._mockupFile) {
          fileToUpload = line._mockupFile;
        }
        // Check for File object in files.mockup.file
        else if (line.files?.mockup?.file) {
          fileToUpload = line.files.mockup.file;
        }
        // Check if designFileUrl is a blob URL (needs to be uploaded)
        else if (line.designFileUrl && line.designFileUrl.startsWith('blob:')) {
          if (line.files?.mockup?.file) {
            fileToUpload = line.files.mockup.file;
          } else {
            console.warn('⚠️ Found blob URL but no File object to upload for inbox line:', line.designName || line.design);
          }
        }

        // Upload mockup file if we have one
        if (fileToUpload) {
          const fileExtension = fileToUpload.name.split('.').pop() || 'png';
          const storageRef = ref(storage, `artwork/${poNumber}/${designNameSanitized}/Mockups/mockup.${fileExtension}`);
          await uploadBytes(storageRef, fileToUpload);
          mockupUrl = await getDownloadURL(storageRef);
          console.log('✅ [OrderInbox] Uploaded mockup to Firebase Storage:', mockupUrl);
        }

        // ===== HANDLE PRODUCTION FILES =====
        let updatedProductionFiles = line.files?.production || [];
        if (updatedProductionFiles.length > 0) {
          const productionUploadPromises = updatedProductionFiles.map(async (prodFile) => {
            if (prodFile.url && prodFile.url.startsWith('blob:') && prodFile.file) {
              try {
                const typeFolder = (prodFile.type || 'Unassigned').replace(/[^a-zA-Z0-9_-]/g, '_');
                const storageRef = ref(storage, `artwork/${poNumber}/${designNameSanitized}/Production/${typeFolder}/${prodFile.name}`);
                await uploadBytes(storageRef, prodFile.file);
                const firebaseUrl = await getDownloadURL(storageRef);
                console.log('✅ [OrderInbox] Uploaded production file to Firebase Storage:', prodFile.name);
                return {
                  name: prodFile.name,
                  url: firebaseUrl,
                  type: prodFile.type || 'unassigned'
                };
              } catch (error) {
                console.error('❌ [OrderInbox] Error uploading production file:', prodFile.name, error);
                return prodFile;
              }
            }
            return {
              name: prodFile.name,
              url: prodFile.url,
              type: prodFile.type || 'unassigned'
            };
          });
          updatedProductionFiles = await Promise.all(productionUploadPromises);
        }

        // Build updated files object
        const updatedFiles = {
          ...(line.files || {}),
          ...(mockupUrl && {
            mockup: {
              ...(line.files?.mockup || {}),
              url: mockupUrl,
              fileName: fileToUpload?.name || line.files?.mockup?.fileName
            }
          }),
          ...(updatedProductionFiles.length > 0 && {
            production: updatedProductionFiles
          })
        };

        const cleanedLine = {
          ...line,
          ...(getPersistableMockupUrl(mockupUrl) && { designFileUrl: getPersistableMockupUrl(mockupUrl) }),
          files: Object.keys(updatedFiles).length > 0 ? updatedFiles : undefined,
          _mockupFile: undefined
        };

        if (cleanedLine.files?.mockup?.file) {
          delete cleanedLine.files.mockup.file;
        }

        return cleanedLine;
      }));

      // Build payload expected by addOrderToDb so artwork/files are handled consistently
      const orderPayload = {
        poNumber,
        projectManager: formData.projectManager || '',
        facilityId: resolveFacilityId(formData.facilityId || activeFacilityId),
        customerId: formData.customerId || null,
        customerName: formData.customerName || '',
        // Let addOrderToDb use server timestamps for startDate; pass dueDate only
        dueDate: formData.dueDate ? new Date(formData.dueDate) : null,
        notes: formData.notes || '',
        totalQuantity,
        subtotal,
        orderTotal: subtotal,
        cogsTotal,
        cogsPercentage: subtotal > 0 ? (cogsTotal / subtotal) * 100 : 0,
        extraFees: 0,
        providedGoods: false,
        sample: false,
        lines: updatedLines.map((line, index) => ({
          designName: line.design || '',
          garmentId: line.style || '',
          garmentColor: line.color || '',
          sizes: Object.fromEntries(
            Object.entries(line.sizes || {}).map(([size, qty]) => [size, Number(qty) || 0])
          ),
          pricePerPiece: Number(line.price) || 0,
          cogs: Number(line.cogs) || 0,
          frontInkColors: line.frontInkColors || [],
          backInkColors: line.backInkColors || [],
          otherInkColors: line.otherInkColors || [],
          printLocations: line.printLocations || [],
          sizeType: line.sizeType || 'unisex',
          type: line.type || 'screen',
          files: line.files || undefined,
          designFileUrl: line.designFileUrl || null,
          lineNumber: index + 1
        }))
      };

      console.log('Creating order via addOrderToDb:', orderPayload);

      const result = await addOrderToDb(orderPayload);
      if (!result.success) {
        throw new Error(result.error || 'Failed to create order');
      }

      const orderId = result.orderId;

      // Add metadata / flags that addOrderToDb doesn't handle
      await updateDoc(doc(db, 'orders', orderId), {
        type: DOC_TYPES.ORDER,
        createdBy: 'AI_PARSER',
        isRush: formData.isRush || false,
        metadata: {
          aiParsedOrderId: originalAiOrder.id,
          confidence: originalAiOrder.confidence,
          originalEmail: originalAiOrder.originalEmail
        }
      });

      // Update the AI parsed order status
      await updateDoc(doc(db, 'aiParsedOrders', originalAiOrder.id), {
        status: 'APPROVED',
        convertedOrderId: orderId,
        updatedAt: serverTimestamp()
      });

      // Refresh the list
      await fetchAiOrders();

      // Close the modal
      setShowModal(false);

      // Navigate to the orders list (individual order view route doesn't exist yet)
      // The new order will appear at the top of the list
      alert(`Order created successfully! Order ID: ${orderId}`);
      navigate('/orders');
    } catch (error) {
      console.error('Error approving order:', error);
      setError(error.message);
      alert('Error creating order: ' + error.message);
    }
  };

  const handleRejectOrder = async (aiOrder) => {
    try {
      await updateDoc(doc(db, 'aiParsedOrders', aiOrder.id), {
        status: 'REJECTED',
        updatedAt: serverTimestamp()
      });

      await fetchAiOrders();
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting order:', error);
      setError(error.message);
    }
  };

  if (loading) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>ORDER INBOX</Title>
          <div style={{ 
            fontSize: '0.85em', 
            color: 'rgba(255, 255, 255, 0.7)', 
            marginTop: '-12px',
            letterSpacing: '0.02em'
          }}>
            Send orders to: <span style={{ color: 'var(--al-primary)', fontWeight: '600' }}>autoorder@eightsevencentral.com</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {networkStatus === 'offline' && (
            <div style={{ color: '#dc3545' }}>OFFLINE MODE</div>
          )}
          {error && (
            <div style={{ color: '#dc3545' }}>Error: {error}</div>
          )}
          {debugInfo && (
            <div style={{ 
              padding: '8px 12px', 
              background: debugInfo.success ? 'var(--al-surface)' : '#4a1f1f',
              border: `1px solid ${debugInfo.success ? 'var(--al-primary)' : '#dc3545'}`,
              borderRadius: '4px',
              fontSize: '0.85em'
            }}>
              {debugInfo.success ? (
                <div>
                  <div style={{ color: 'var(--al-primary)' }}>✓ {debugInfo.message || 'Emails processed'}</div>
                  {debugInfo.resultsCount > 0 && (
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9em', marginTop: '4px' }}>
                      {debugInfo.resultsCount} email(s) processed
                    </div>
                  )}
                  {debugInfo.resultsCount === 0 && (
                    <div style={{ color: '#ffa726', fontSize: '0.9em', marginTop: '4px' }}>
                      No new emails found
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#dc3545' }}>
                  ✗ {debugInfo.error}
                  {debugInfo.details && (
                    <div style={{ fontSize: '0.85em', marginTop: '4px', color: 'rgba(255,255,255,0.7)' }}>
                      {debugInfo.details}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <RefreshButton 
            onClick={fetchAiOrders} 
            disabled={processingEmails || networkStatus === 'offline'}
          >
            {processingEmails ? 'Processing...' : 'Refresh'}
          </RefreshButton>
        </div>
      </Header>

      {aiOrders.length === 0 && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: 'rgba(255, 255, 255, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          marginTop: '20px'
        }}>
          <div style={{ fontSize: '1.2em', marginBottom: '10px' }}>No orders with status 'AI_PARSED'</div>
          <div style={{ fontSize: '0.9em', marginTop: '10px' }}>
            Check the console for details about orders in Firestore and their statuses.
          </div>
        </div>
      )}

      {aiOrders.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Customer</Th>
              <Th>Quantity</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {aiOrders.map((order) => (
              <TableRow 
                key={order.id}
                onClick={() => handleViewOrder(order)}
              >
                <Td>{formatDate(order.originalEmail?.receivedAt)}</Td>
                <Td>
                  {order.parsedOrder?.customerName || 'N/A'}
                </Td>
                <Td>
                  {order.parsedOrder?.lines?.reduce((total, line) => total + (line.totalQuantity || 0), 0) || 0} pcs
                  {order.parsedOrder?.lines?.length > 1 && (
                    <div style={{ fontSize: '0.7em', color: '#666' }}>
                      ({order.parsedOrder.lines.length} designs)
                    </div>
                  )}
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  <ActionButton onClick={() => handleViewOrder(order)} title="View">
                    <FaEye />
                  </ActionButton>
                  {order.status === 'AI_PARSED' && (
                    <>
                      <ActionButton 
                        onClick={() => handleApproveOrder(order)} 
                        title="Approve"
                        style={{ color: '#28a745' }}
                      >
                        <FaCheckCircle />
                      </ActionButton>
                      <ActionButton 
                        onClick={() => handleRejectOrder(order)} 
                        title="Reject"
                        style={{ color: '#dc3545' }}
                      >
                        <FaTimesCircle />
                      </ActionButton>
                    </>
                  )}
                </Td>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

      {selectedOrder && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <OrderIntakeForm
            parsedOrder={selectedOrder}
            onClose={() => setShowModal(false)}
            onApprove={handleApproveOrder}
            onReject={handleRejectOrder}
          />
        </Modal>
      )}
    </Container>
  );
};

export default OrderInbox;